import { ColDef } from "ag-grid-community";
import { Popover } from "antd";
import dayjs from "dayjs";
import useBillingStore from "../../stores/billing-store/billing-store";
import { TReceiptEntity } from "../../types/billing";
import ColumnSelector from "../common/column-selector";
import PaymodeFilterDropdown from "./paymode-filter-dropdown";
import ServiceFilterDropdown from "./service-filter-dropdown";
import {
  CircleXMarkSolidIcon,
  MagnifyingGlassRegularIcon,
} from "@elixir/icons";
import TooltipComponent from "../common/tooltip-component";
import queryClient from "../../instances/react-query-client";
import { getAppointmentsQueryNameV3 } from "../../payment-dashboard-v2/api/useGetAllAppointmentsV2";
import { getAclViewClassName } from "@elixir/utils";
import { SUBJECT_KEYS } from "@elixir/types";

export default function PaymentsDashboardFilterBar() {
  const {
    searchFilterString,
    setSearchFilterString,
    lastUpdatedAt,
    setLastUpdatedAt,
  } = useBillingStore();


  const onDownloadReportClick = () => {
    window.open("https://analytics.eka.care", "_blank");
  };

  const handleRefresh = async () => {
    await queryClient.invalidateQueries(getAppointmentsQueryNameV3());
    setLastUpdatedAt(new Date());
  };

  const formatUpdatedAt = (date: Date | null) => {
    if (!date) return "";
    return dayjs(date).format("Do MMM h:mma");
  };

  return (
    <div className="flex items-center justify-between w-full pl-12 pr-16 py-8 bg-neutral-9">
      <div className="relative flex items-center px-8 space-x-8 bg-white border border-neutral-7 rounded-2 w-20p">
        <div className="flex items-center justify-center w-16 h-16">
          <MagnifyingGlassRegularIcon className="w-12 h-12 fill-neutral-6" />
        </div>
        <input
          value={searchFilterString || ""}
          type="search"
          name="ether-search-component"
          autoComplete="off"
          className="w-full py-4 bg-transparent focus:outline-none placeholder-neutral-6 text-11 input-cancelable-on-filled"
          placeholder="Search by Phone / Name / UHID"
          onChange={(e) => {
            setSearchFilterString(e.target.value);
          }}
        />
        <div className="absolute inset-0 pointer-events-none"></div>
        <div
          className="absolute flex items-center justify-center w-16 h-16 transform -translate-y-1/2 right-8 top-1/2 cancel-icon cursor-pointer"
          onClick={() => setSearchFilterString("")}
        >
          <CircleXMarkSolidIcon className="w-12 h-12 fill-neutral-black" />
        </div>
      </div>
      <div className="flex items-center space-x-16">
        {lastUpdatedAt && (
          <div className="flex items-center space-x-8 text-11 text-neutral-5">
            <span>Last Updated At: {formatUpdatedAt(lastUpdatedAt)}</span>
            <TooltipComponent title="Refresh now" placement="top">
              <button
                className="flex items-center justify-center w-24 h-24 cursor-pointer rounded-full transition-all duration-150 flex-shrink-0"
                onClick={handleRefresh}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 512 512"
                  fill="currentColor"
                  className="dr-text-darwin-primary-primary dr-w-14 dr-h-14 "
                >
                  <path d="M142.9 142.9c-17.5 17.5-30.1 38-37.8 59.8c-5.9 16.7-24.2 25.4-40.8 19.5s-25.4-24.2-19.5-40.8C55.6 150.7 73.2 122 97.6 97.6c87.2-87.2 228.3-87.5 315.8-1L455 55c6.9-6.9 17.2-8.9 26.2-5.2s14.8 12.5 14.8 22.2l0 128c0 13.3-10.7 24-24 24l-8.4 0c0 0 0 0 0 0L344 224c-9.7 0-18.5-5.8-22.2-14.8s-1.7-19.3 5.2-26.2l41.1-41.1c-62.6-61.5-163.1-61.2-225.3 1zM16 312c0-13.3 10.7-24 24-24l7.6 0 .7 0L168 288c9.7 0 18.5 5.8 22.2 14.8s1.7 19.3-5.2 26.2l-41.1 41.1c62.6 61.5 163.1 61.2 225.3-1c17.5-17.5 30.1-38 37.8-59.8c5.9-16.7 24.2-25.4 40.8-19.5s25.4 24.2 19.5 40.8c-10.8 30.6-28.4 59.3-52.9 83.8c-87.2 87.2-228.3 87.5-315.8 1L57 457c-6.9 6.9-17.2 8.9-26.2 5.2S16 449.7 16 440l0-119.6 0-.7 0-7.6z"></path>
                </svg>
              </button>
            </TooltipComponent>
          </div>
        )}
        <PaymodeFilterDropdown />
        <ServiceFilterDropdown />
        <BillingColumnSelector />
        <p
          className={`cursor-pointer text-actionPrimary font-700 text-13 whitespace-nowrap ${getAclViewClassName(SUBJECT_KEYS.ANALYTICS)}`}
          onClick={onDownloadReportClick}
        >
          Download Report
        </p>
      </div>
    </div>
  );
}

function BillingColumnSelector() {
  const {
    queueColumnDefinitions,
    setQueueColumnDefinitions,
    showColumnSelector,
    setShowColumnSelector,
  } = useBillingStore(
    ({
      queueColumnDefinitions,
      setQueueColumnDefinitions,
      showColumnSelector,
      setShowColumnSelector,
    }) => ({
      queueColumnDefinitions,
      setQueueColumnDefinitions,
      showColumnSelector,
      setShowColumnSelector,
    })
  );
  return (
    <Popover
      overlayClassName="popover-p-0"
      overlayInnerStyle={{ borderRadius: "8px", overflow: "hidden" }}
      placement={"bottomLeft"}
      destroyTooltipOnHide={true}
      visible={showColumnSelector}
      onVisibleChange={setShowColumnSelector}
      trigger={"click"}
      content={
        <ColumnSelector<string>
          options={
            queueColumnDefinitions?.map((prop: ColDef<TReceiptEntity>) => {
              const { colId, headerName } = prop;
              return { label: headerName!, value: colId! };
            }) || []
          }
          mandatoryColumns={["idx", "pt-details", "actions"]}
          preSelectedColumns={
            queueColumnDefinitions?.reduce<string[]>(
              (prev, curr: ColDef<TReceiptEntity>) => {
                if (curr.colId && !curr.hide) {
                  prev.push(curr.colId);
                }
                return prev;
              },
              []
            ) || []
          }
          applySelectedColumns={(colIds) => {
            const colIdsSet = new Set(colIds);

            setQueueColumnDefinitions(
              queueColumnDefinitions?.map((prop: ColDef<TReceiptEntity>) => {
                if (prop.colId && colIdsSet.has(prop.colId)) {
                  return { ...prop, hide: false };
                } else {
                  return { ...prop, hide: true };
                }
              })
            );
          }}
          closeCallback={() => {
            setShowColumnSelector(false);
          }}
        />
      }
    >
      <div className="flex items-center justify-center w-16 h-16 cursor-pointer">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 512 512"
          className="w-16 h-16 fill-neutral-5"
        >
          <path d="M487.4 315.7l-42.6-24.6c4.3-23.2 4.3-47 0-70.2l42.6-24.6c4.9-2.8 7.1-8.6 5.5-14-11.1-35.6-30-67.8-54.7-94.6-3.8-4.1-10-5.1-14.8-2.3L380.8 110c-17.9-15.4-38.5-27.3-60.8-35.1V25.8c0-5.6-3.9-10.5-9.4-11.7-36.7-8.2-74.3-7.8-109.2 0-5.5 1.2-9.4 6.1-9.4 11.7V75c-22.2 7.9-42.8 19.8-60.8 35.1L88.7 85.5c-4.9-2.8-11-1.9-14.8 2.3-24.7 26.7-43.6 58.9-54.7 94.6-1.7 5.4.6 11.2 5.5 14L67.3 221c-4.3 23.2-4.3 47 0 70.2l-42.6 24.6c-4.9 2.8-7.1 8.6-5.5 14 11.1 35.6 30 67.8 54.7 94.6 3.8 4.1 10 5.1 14.8 2.3l42.6-24.6c17.9 15.4 38.5 27.3 60.8 35.1v49.2c0 5.6 3.9 10.5 9.4 11.7 36.7 8.2 74.3 7.8 109.2 0 5.5-1.2 9.4-6.1 9.4-11.7v-49.2c22.2-7.9 42.8-19.8 60.8-35.1l42.6 24.6c4.9 2.8 11 1.9 14.8-2.3 24.7-26.7 43.6-58.9 54.7-94.6 1.5-5.5-.7-11.3-5.6-14.1zM256 336c-44.1 0-80-35.9-80-80s35.9-80 80-80 80 35.9 80 80-35.9 80-80 80z"></path>
        </svg>
      </div>
    </Popover>
  );
}
